import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import { SmartLink } from "../lib/navLink.jsx";

/**
 * AWS-style peek carousel.
 *
 * When loop=true (default): clones of the last/first slides are prepended/appended
 * so peeks are always visible on both sides. After animating into a clone the track
 * instantly jumps to the real counterpart (no visible glitch).
 *
 * When loop=false: prev is disabled on the first slide, next on the last.
 *
 * section props: id, kicker, title, loop (bool, default true),
 *   slides: [{ title, body, category, imageUrl, imageAlt, linkLabel, linkHref }]
 */
export default function SectionCarousel({ section }) {
  if (!section?.slides?.length) return null;
  const { id, kicker, title, slides } = section;
  const loop = section.loop !== false;
  const total = slides.length;

  // When loop and more than 1 slide: [clone_last, ...slides, clone_first]
  const renderItems = useMemo(() => {
    if (!loop || total <= 1) {
      return slides.map((s, i) => ({ slide: s, origIdx: i }));
    }
    return [
      { slide: slides[total - 1], origIdx: total - 1, isClone: true },
      ...slides.map((s, i) => ({ slide: s, origIdx: i })),
      { slide: slides[0], origIdx: 0, isClone: true },
    ];
  }, [slides, loop, total]);

  // Convert logical index → virtual index in renderItems
  const toV = useCallback(
    (logicalIdx) => (loop && total > 1 ? logicalIdx + 1 : logicalIdx),
    [loop, total],
  );

  const [aIndex, setAIndex] = useState(0);           // logical active index
  const [vIndex, setVIndex] = useState(toV(0));       // virtual index (track position)
  const [jumping, setJumping] = useState(false);      // true → suppress CSS transition
  const [translateX, setTranslateX] = useState(0);
  const stageRef = useRef(null);
  const jumpTimerRef = useRef(null);
  const GAP = 16;

  const recalculate = useCallback((vi) => {
    const stage = stageRef.current;
    if (!stage) return;
    const stageW = stage.offsetWidth;
    const slideW = Math.round(stageW * 0.68);
    setTranslateX((stageW - slideW) / 2 - vi * (slideW + GAP));
  }, []);

  useLayoutEffect(() => {
    recalculate(vIndex);
    const stage = stageRef.current;
    if (!stage) return;
    const ro = new ResizeObserver(() => recalculate(vIndex));
    ro.observe(stage);
    return () => ro.disconnect();
  }, [vIndex, recalculate]);

  // Reset to slide 0 when loop or slide count changes
  useEffect(() => {
    if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
    setAIndex(0);
    setVIndex(toV(0));
    setJumping(false);
  }, [loop, total, toV]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current); }, []);

  // After animating into a clone, instantly jump to the real counterpart.
  function scheduleJump(realVIndex) {
    if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
    jumpTimerRef.current = setTimeout(() => {
      setJumping(true);
      setVIndex(realVIndex);
      // Double rAF: first lets React flush the no-transition render,
      // second lets the browser paint before re-enabling the transition.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setJumping(false))
      );
    }, 420); // matches CSS transition duration
  }

  const prev = useCallback(() => {
    if (!loop && aIndex === 0) return;
    const newA = (aIndex - 1 + total) % total;
    setAIndex(newA);
    if (loop && total > 1 && aIndex === 0) {
      // Animate to clone of last (vIndex 0), then jump to real last (vIndex total)
      setVIndex(0);
      scheduleJump(total);
    } else {
      setVIndex(toV(newA));
    }
  }, [loop, aIndex, total, toV]);

  const next = useCallback(() => {
    if (!loop && aIndex === total - 1) return;
    const newA = (aIndex + 1) % total;
    setAIndex(newA);
    if (loop && total > 1 && aIndex === total - 1) {
      // Animate to clone of first (vIndex total+1), then jump to real first (vIndex 1)
      setVIndex(total + 1);
      scheduleJump(1);
    } else {
      setVIndex(toV(newA));
    }
  }, [loop, aIndex, total, toV]);

  const atStart = !loop && aIndex === 0;
  const atEnd = !loop && aIndex === total - 1;

  return (
    <section className="pmp-carousel-section" id={id}>
      <Container>
        <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 mb-4">
          <div>
            {kicker && <p className="pmp-kicker mb-2">{kicker}</p>}
            {title && <h2 className="h3 fw-bold mb-0">{title}</h2>}
          </div>
        </div>
      </Container>

      <div className="pmp-peek-stage" ref={stageRef}>
        <div
          className="pmp-peek-track"
          style={{
            transform: `translateX(${translateX}px)`,
            transition: jumping ? "none" : undefined,
          }}
        >
          {renderItems.map(({ slide, origIdx, isClone }, vi) => {
            const isActive = vi === vIndex;
            return (
              <div
                key={`${isClone ? "clone" : "real"}-${origIdx}-${vi}`}
                className={`pmp-peek-slide-item${isActive ? " pmp-peek-active" : ""}`}
                onClick={() => {
                  if (vi < vIndex) prev();
                  else if (vi > vIndex) next();
                }}
              >
                <div className="pmp-peek-slide-inner">
                  {slide.imageUrl ? (
                    <img src={slide.imageUrl} alt={slide.imageAlt ?? slide.title} />
                  ) : (
                    <div className="pmp-peek-slide-placeholder">
                      {slide.imageAlt ?? slide.title}
                    </div>
                  )}

                  <div className="pmp-peek-slide-overlay" aria-hidden="true" />

                  {slide.category && (
                    <span className="pmp-peek-cat-badge">{slide.category}</span>
                  )}

                  <div className="pmp-peek-slide-content">
                    <div className="pmp-peek-slide-title">{slide.title}</div>
                    {slide.body && (
                      <div className="pmp-peek-slide-body">{slide.body}</div>
                    )}
                    {slide.linkLabel && (
                      <SmartLink
                        href={slide.linkHref || "#"}
                        className="pmp-peek-slide-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {slide.linkLabel}
                        <span className="bi bi-arrow-right" aria-hidden="true" />
                      </SmartLink>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Container>
        <div className="pmp-peek-nav-wrapper">
          <div className="pmp-peek-nav">
            <button
              className="pmp-peek-nav-btn"
              aria-label="Previous slide"
              onClick={prev}
              disabled={atStart}
            >
              <span className="bi bi-chevron-left" aria-hidden="true" />
            </button>
            <span className="pmp-peek-nav-counter">
              {aIndex + 1} / {total}
            </span>
            <button
              className="pmp-peek-nav-btn"
              aria-label="Next slide"
              onClick={next}
              disabled={atEnd}
            >
              <span className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
}
